import { create } from 'zustand';
import { Client } from '../../types/types';
import { Subscription } from '@/types/subscription';
import { getUserGymId } from '@/utils/gymAuth';
import * as clientsService from '@/services/clientsService';

interface ClientsState {
  clients: Client[];
  selectedClient: Client | null;
  isLoading: boolean;
  error: string | null;
  currentGymId: string | null; // Track which gym the clients belong to
  
  fetchClients: () => Promise<void>;
  fetchClientsByIds: (userIds: string[] | null) => Promise<void>;
  fetchClientById: (id: string) => Promise<void>;
  updateClient: (id: string, updates: Partial<Client>) => Promise<boolean>;
  deleteClient: (id: string) => Promise<boolean>;
  setSelectedClient: (client: Client | null) => void;
  clearClients: () => void; // Clear all clients on gym switch
}

function getLatestSubscription(
  subscriptions: Subscription[] | null | undefined
): Subscription | null {
  if (!subscriptions?.length) return null;

  const statusRank = (s: unknown) => {
    const v = String(s ?? "").toLowerCase();
    // paused ≡ active
    if (v === "active" || v === "paused") return 2;
    if (v === "expired") return 0;
    // statut inconnu en dernier
    return -1;
  };

  const dateMs = (iso?: string | null) =>
    iso ? Date.parse(iso) || -Infinity : -Infinity;

  // Tri: created_at desc, puis statut desc, puis updated_at/start_date en secours
  const sorted = [...subscriptions].sort((a, b) => {
    const aCreated = dateMs(a.created_at) || dateMs(a.start_date) || dateMs(a.updated_at);
    const bCreated = dateMs(b.created_at) || dateMs(b.start_date) || dateMs(b.updated_at);

    if (bCreated !== aCreated) return bCreated - aCreated;

    const byStatus = statusRank(b.status) - statusRank(a.status);
    if (byStatus !== 0) return byStatus;

    // Tiebreakers supplémentaires (facultatifs)
    const byUpdated = dateMs(b.updated_at) - dateMs(a.updated_at);
    if (byUpdated !== 0) return byUpdated;

    return dateMs(b.start_date) - dateMs(a.start_date);
  });

  return sorted[0] ?? null;
}

export const useClientsSupabaseStore = create<ClientsState>((set, get) => ({
  clients: [],
  selectedClient: null,
  isLoading: false,
  error: null,
  currentGymId: null,

  // ✅ Clear all clients on gym switch
  clearClients: () => {
    console.log('[clientsSupabaseStore] Clearing all clients cache');
    set({ clients: [], selectedClient: null, error: null, currentGymId: null });
  },

  fetchClients: async () => {
    await get().fetchClientsByIds(null);
  },

  fetchClientsByIds: async (userIds: string[] | null) => {
    set({ isLoading: true, error: null });
    try {
      const gymId = getUserGymId();
      const currentGymId = get().currentGymId;
      
      // ✅ If gym changed, clear cache first
      if (currentGymId && currentGymId !== gymId) {
        console.log('[clientsSupabaseStore] Gym changed, clearing cache');
        set({ clients: [], currentGymId: gymId });
      }
      
      // Call edge function to fetch clients
      const clients = await clientsService.getClients(gymId, userIds);
      
      // Transform to match existing Client interface
      const formattedData: Client[] = clients.map((client: any) => {
        const latestSub = client.subscription;
        return {
          id: client.id,
          firstName: client.firstName || '',
          lastName: client.lastName || '',
          email: client.email || '',
          phone: client.phone || '',
          status: latestSub?.is_paused ? "Paused" : latestSub?.status ?? "Inactive",
          address: client.address || '',
          birthDate: client.birthDate || '',
          emergencyContact: client.emergencyContact || '',
          clothingSize: client.clothingSize || '',
          notes: client.notes || '',
          createdAt: latestSub?.created_at ?? undefined,
          // Un client sans abonnement n'a ni date de début ni date de fin.
          // Le repli sur `new Date()` faisait apparaître tous ces clients dans
          // le premier seau de la heatmap « Expirations – 8 semaines » et leur
          // donnait « 0 jour restant ».
          startDate: latestSub?.start_date ?? undefined,
          expiryDate: latestSub?.end_date ?? undefined,
          membershipType: latestSub?.type ?? "Standard",
          sessionsTotal: latestSub?.sessions_total,
          sessionsRemaining: latestSub?.sessions_remaining,
          jokersTotal: latestSub?.jokers_total,
          jokersRemaining: latestSub?.jokers_remaining,
          subscriptionHistory: client.allSubscriptions || [],
          allowed_courses: client.allowed_courses ?? null,
          lastBookingAt: client.lastBookingAt ?? null,
          gymId: gymId
        };
      });
      
      // ✅ If userIds is null (full refresh), REPLACE the cache entirely
      if (userIds === null) {
        set({ clients: formattedData, currentGymId: gymId });
      } else {
        // Otherwise, concatenate only new clients (for partial fetches)
        const existingClients = get().clients;
        const existingClientIds = new Set(existingClients.map(c => c.id));
        const newClients = formattedData.filter(c => !existingClientIds.has(c.id));
        set({ clients: [...existingClients, ...newClients], currentGymId: gymId });
      }
    } catch (error) {
      console.error('[clientsSupabaseStore] Error fetching clients:', error);
      set({ error: 'Failed to fetch clients' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  fetchClientById: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const gymId = getUserGymId();
      
      // Call edge function to fetch single client
      const client = await clientsService.getClientById(gymId, id);
      
      const latestSub = client.subscription;

      // `get_client_by_id` ne renvoie pas la dernière réservation ; on la
      // reprend de la liste déjà chargée plutôt que d'inventer une date.
      const cached = get().clients.find((c) => c.id === id);
      const lastBookingAt: string | null = client.lastBookingAt ?? cached?.lastBookingAt ?? null;

      // Format client data with subscription info
      const clientData: Client = {
        id: client.id,
        firstName: client.firstName || '',
        lastName: client.lastName || '',
        email: client.email || '',
        phone: client.phone || '',
        status: latestSub ? (latestSub.is_paused ? "Paused" : latestSub.status || "Inactive") : "Inactive",
        address: client.address || '',
        birthDate: client.birthDate || '',
        emergencyContact: client.emergencyContact || '',
        clothingSize: client.clothingSize || '',
        notes: client.notes || '',
        createdAt: latestSub?.created_at ?? undefined,
        membershipType: latestSub?.type || "Standard",
        // Idem liste : pas d'abonnement => pas de dates, plutôt que la date du
        // jour qui faisait passer le client pour « expirant aujourd'hui ».
        startDate: latestSub?.start_date ?? undefined,
        expiryDate: latestSub?.end_date ?? undefined,
        sessionsRemaining: latestSub?.sessions_remaining || 0,
        jokersRemaining: latestSub?.jokers_remaining || 0,
        subscriptionHistory: client.allSubscriptions || [],
        sessionsTotal: latestSub?.sessions_total || 0,
        jokersTotal: latestSub?.jokers_total || 0,
        profilePhotoUrl: client.profilePhotoUrl,
        // `lastVisit: new Date()` était codé en dur : le serveur ne renvoie
        // aucune date de visite. On expose la dernière réservation connue —
        // le seul signal réel — et rien du tout si on ne l'a pas.
        lastVisit: lastBookingAt ?? undefined,
        lastBookingAt,
        allowed_courses: (client as any).allowed_courses ?? null,
        gymId: gymId || ''
      };

      set({ selectedClient: clientData });
    } catch (error) {
      console.error('[clientsSupabaseStore] Error fetching client:', error);
      // Sans ce reset, la fiche du client précédent restait affichée sous
      // l'URL du nouveau, et les dialogues opéraient sur le mauvais client.id.
      set({ error: 'Failed to fetch client', selectedClient: null });
    } finally {
      set({ isLoading: false });
    }
  },
  
  updateClient: async (id, updates) => {
    set({ isLoading: true, error: null });
    try {
      const gymId = getUserGymId();
      
      // Prepare client data for edge function
      const clientData: clientsService.ClientData = {
        id,
        firstName: updates.firstName,
        lastName: updates.lastName,
        email: updates.email,
        phone: updates.phone,
        address: updates.address,
        birthDate: updates.birthDate,
        emergencyContact: updates.emergencyContact,
        clothingSize: updates.clothingSize,
        notes: updates.notes,
      };
      
      // Call edge function to update client
      await clientsService.updateClient(gymId, id, clientData);
      
      // Update subscription if needed
      if (updates.membershipType !== undefined || 
          updates.startDate !== undefined || 
          updates.expiryDate !== undefined ||
          updates.status !== undefined ||
          updates.sessionsRemaining !== undefined ||
          updates.jokersRemaining !== undefined) {
        
        const subscriptionData: clientsService.SubscriptionData = {
          type: updates.membershipType || 'Standard',
          startDate: updates.startDate || new Date().toISOString(),
          endDate: updates.expiryDate || new Date().toISOString(),
          price: 0, // Default value
          sessionsTotal: updates.sessionsTotal,
          sessionsRemaining: updates.sessionsRemaining,
          jokersTotal: updates.jokersTotal,
          jokersRemaining: updates.jokersRemaining,
          status: updates.status,
        };
        
        await clientsService.updateSubscription(gymId, id, subscriptionData);
      }
      
      // Refresh the client data
      if (get().selectedClient?.id === id) {
        await get().fetchClientById(id);
      }
      
      await get().fetchClients();
      return true;
    } catch (error) {
      console.error('[clientsSupabaseStore] Failed to update client:', error);
      set({ error: 'Failed to update client' });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },
  
  deleteClient: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const gymId = getUserGymId();
      
      // Call edge function to delete client
      await clientsService.deleteClient(gymId, id);
      
      // Refresh the clients list
      await get().fetchClients();
      
      // Clear selected client if it was deleted
      if (get().selectedClient?.id === id) {
        set({ selectedClient: null });
      }
      
      return true;
    } catch (error) {
      console.error('[clientsSupabaseStore] Failed to delete client:', error);
      set({ error: 'Failed to delete client' });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },
  
  setSelectedClient: (client) => {
    set({ selectedClient: client });
  }
}));
